import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from '../ui/Select';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { LuCode, LuLoaderCircle, LuRefreshCw } from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { useListUrlState } from '../../hooks/useListUrlState';
import { useDebounce } from '../../hooks/useDebounce';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import CreateTestCaseModal from '../test-suites/CreateTestCaseModal';
import CreateTestCaseOptionsModal from '../test-suites/CreateTestCaseOptionsModal';
import CompileScriptsOptionsModal, {
  SCRIPT_GENERATION_STARTED_MESSAGE,
} from '../test-suites/CompileScriptsOptionsModal';
import ExtensionRecordingModal from '../test-suites/ExtensionRecordingModal';
import ImportTestCasesModal from '../test-suites/ImportTestCasesModal';
import RunTestCaseModal from '../test-suites/RunTestCaseModal';
import EditDiscoveryStepsModal from '../test-suites/EditDiscoveryStepsModal';
import RunDiscoveryTestCaseModal from '../wizard/steps/discovery/RunDiscoveryTestCaseModal';
import {
  fetchLatestDiscoveryRunId,
  getStoredDiscoveryRunId,
} from '../../utils/discoveryRunStorage';
import { refreshNotifications } from '../../utils/notificationEvents';
import {
  Breadcrumbs,
  BulkActionBar,
  ConfirmDialog,
  DataTable,
  ErrorState,
  FilterBar,
  PageScaffold,
  Pagination,
  PriorityBadge,
  RowActions,
  SearchInput,
  StatusBadge,
} from '../ui';

const LIST_SCHEMA = {
  page: 1,
  page_size: 20,
  search: '',
  status: 'all',
  priority: 'all',
  type: 'all',
};

const STATUS_FILTERS = ['passed', 'failed', 'pending'];
const PRIORITY_FILTERS = ['high', 'medium', 'low'];
// Mirrors backend TestCaseType: MANUAL = 1, RECORDED = 2, AI_GENERATED = 3
const TYPE_FILTERS = {
  manual: { value: 1, label: 'Manual' },
  recorded: { value: 2, label: 'Recorded' },
  ai: { value: 3, label: 'AI Generated' },
};

// Manual and AI generated test cases are compiled, edited and run through the
// UI Discovery flow; recorded test cases keep the extension recording flow.
const DISCOVERY_FLOW_TEST_CASE_TYPES = [TYPE_FILTERS.manual.value, TYPE_FILTERS.ai.value];
const usesDiscoveryFlow = (testCase) =>
  DISCOVERY_FLOW_TEST_CASE_TYPES.includes(Number(testCase?.test_case_type));

const mapCase = (tc, projectId) => ({
  id: tc.test_case_id || tc.id,
  test_case_id: tc.test_case_id || tc.id,
  name: tc.name,
  status: tc.status,
  priority: tc?.priority?.toLowerCase?.() || tc?.priority,
  suiteId: tc.suite_id,
  suiteName: tc.suite_name || (Array.isArray(tc.test_suites)
    ? tc.test_suites.map((s) => s.suite_name).filter(Boolean).join(', ')
    : null),
  project_id: projectId || tc.project_id,
  description: tc.description,
  automation_status: tc.automation_status,
  execution_time: tc.execution_time,
  environment: tc.environment,
  environment_id: tc.environment_id,
  browser: tc.browser,
  device: tc.device,
  tags: tc.tags,
  created_by: tc.created_by,
  created_at: tc.created_at,
  updated_at: tc.updated_at,
  ai_generated: tc.ai_generated,
  is_healed: tc.is_healed,
  test_case_type: tc.test_case_type,
  last_result: tc.last_execution_status || tc.last_result || tc.execution_status,
  steps: tc.steps || {
    step_description: [],
    expected_result: [],
    prerequisites: [],
    additional_notes: [],
  },
});

/**
 * BUILD · Test Cases — URL-persistent list with modern pagination & actions.
 */
const CasesWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const projectId = getProjectIdFromURL() || selectedProject?.id;
  const projectName = selectedProject?.name || 'Project';

  const {
    page,
    page_size: pageSize,
    search,
    status: rawStatus,
    priority: rawPriority,
    type: rawType,
    setPage,
    setPageSize,
    setSearch,
    setParam,
    clearFilters,
    activeFilterCount,
    pageSizeOptions,
  } = useListUrlState(LIST_SCHEMA);

  const status = STATUS_FILTERS.includes(rawStatus) ? rawStatus : 'all';
  const priority = PRIORITY_FILTERS.includes(String(rawPriority).toLowerCase())
    ? String(rawPriority).toLowerCase()
    : 'all';
  const type = TYPE_FILTERS[rawType] ? rawType : 'all';

  const [searchDraft, setSearchDraft] = useState(search);
  const debouncedSearch = useDebounce(searchDraft, 350);

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  useEffect(() => {
    if (debouncedSearch !== search) {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);

  const [createOptionsOpen, setCreateOptionsOpen] = useState(false);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [extensionOpen, setExtensionOpen] = useState(false);
  const [extensionMode, setExtensionMode] = useState('create');
  const [editingTestCaseId, setEditingTestCaseId] = useState(null);
  const [runCase, setRunCase] = useState(null);
  const [deleteCase, setDeleteCase] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [stepsEditCase, setStepsEditCase] = useState(null);
  const [compilingCaseId, setCompilingCaseId] = useState(null);
  const [compilingAll, setCompilingAll] = useState(false);
  const [compileOptionsOpen, setCompileOptionsOpen] = useState(false);
  const recordingRefreshPending = useRef(false);

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)) || 1);

  const navigateTo = useCallback(
    (path) => {
      const workspaceId = getWorkspaceIdFromURL();
      if (workspaceId) {
        navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
      } else {
        navigate(`/dashboard/${path}`);
      }
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  const load = useCallback(async () => {
    if (!projectId) {
      setCases([]);
      setLoading(false);
      setError('Select a project to manage test cases.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = {
        project_id: projectId,
        page,
        page_size: pageSize,
      };
      if (search.trim()) params.search = search.trim();
      if (status !== 'all') params.test_status = status;
      if (priority !== 'all') {
        params.priority_filter = priority.charAt(0).toUpperCase() + priority.slice(1);
      }
      if (type !== 'all') params.test_case_type = TYPE_FILTERS[type].value;

      const res = await axiosInstance.get('/test_case/cases_list', { params });
      const list = Array.isArray(res?.data) ? res.data : [];
      setCases(list.map((tc) => mapCase(tc, projectId)));
      setTotal(res?.total || list.length);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load test cases');
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, pageSize, search, status, priority, type]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && recordingRefreshPending.current) {
        recordingRefreshPending.current = false;
        load();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  useEffect(() => {
    setSelectedIds([]);
  }, [page, pageSize, search, status, priority, type, projectId]);

  // Cache the project's latest discovery run id in localStorage for compile script
  useEffect(() => {
    if (!projectId) return;
    fetchLatestDiscoveryRunId(projectId).catch((err) =>
      console.error('Error fetching discovery run id:', err)
    );
  }, [projectId]);

  // Stable so RunDiscoveryTestCaseModal keeps its agent listener during a local run
  const handleDiscoveryRunSuccess = useCallback(() => {
    load();
  }, [load]);

  const metrics = useMemo(() => {
    const passed = cases.filter((c) =>
      ['passed', 'pass', 'active'].includes(String(c.status || '').toLowerCase())
    ).length;
    const failed = cases.filter((c) =>
      ['failed', 'fail'].includes(String(c.status || '').toLowerCase())
    ).length;
    const pending = cases.filter((c) =>
      ['pending', 'draft', 'review'].includes(String(c.status || '').toLowerCase())
    ).length;
    return { passed, failed, pending };
  }, [cases]);

  const passRate = useMemo(() => {
    const denom = metrics.passed + metrics.failed;
    if (!denom) return total ? '—' : '0%';
    return `${Math.round((metrics.passed / denom) * 100)}%`;
  }, [metrics, total]);

  const openCase = (row) => {
    const listQs = location.search.replace(/^\?/, '');
    const ret = listQs ? `&ret=${encodeURIComponent(listQs)}` : '';
    navigateTo(`test-cases/${row.id}?project_id=${projectId}${ret}`);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === cases.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(cases.map((c) => c.id));
    }
  };

  const confirmDelete = async () => {
    if (!deleteCase) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/test_case/${deleteCase.id}`);
      setDeleteCase(null);
      await load();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to delete test case');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => axiosInstance.delete(`/test_case/${id}`)));
      setSelectedIds([]);
      await load();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to delete selected cases');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    if (!projectId) return;
    setExporting(true);
    try {
      const blobData = await axiosInstance.get('/test_case/export_test_cases', {
        params: { project_id: projectId },
        responseType: 'blob',
      });
      const blob = blobData instanceof Blob ? blobData : new Blob([blobData]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `test-cases-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Uses the run id cached in localStorage, refetching it in case the project
  // was crawled since. Sends the user to crawl the project when it has no run.
  const resolveDiscoveryRunId = async () => {
    let runId = getStoredDiscoveryRunId(projectId);
    if (!runId) {
      try {
        runId = await fetchLatestDiscoveryRunId(projectId);
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch UI discovery run. Please try again.');
        return null;
      }
    }

    if (!runId) {
      toast.info('Run UI discovery for this project before generating scripts.');
      navigateTo(`project-crawl/${projectId}`);
      return null;
    }

    return runId;
  };

  const handleCompileScript = async (row) => {
    setCompilingCaseId(row.id);
    try {
      const runId = await resolveDiscoveryRunId();
      if (!runId) return;

      await axiosInstance.post(
        '/crawler/generate_cwral_script',
        { discovery_run_id: runId },
        { params: { test_case_id: row.id } }
      );
      toast.success(`Script generated for "${row.name}"`);
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Failed to compile script. Please try again.');
    } finally {
      setCompilingCaseId(null);
    }
  };

  // Starts background script generation for the project's manual (1) or AI
  // generated (3) test cases; a null testCaseType compiles both. API errors are
  // rethrown so CompileScriptsOptionsModal shows them and the user can retry.
  const handleCompileAllScripts = async (testCaseType) => {
    if (!projectId) return;
    setCompilingAll(true);
    try {
      const runId = await resolveDiscoveryRunId();
      if (!runId) {
        setCompileOptionsOpen(false);
        return;
      }

      const payload = { discovery_run_id: runId };
      if (testCaseType) payload.test_case_type = testCaseType;
      const response = await axiosInstance.post('/crawler/generate/crwal/project/script', payload, {
        params: { project_id: projectId },
      });
      setCompileOptionsOpen(false);
      toast.success(response?.message || SCRIPT_GENERATION_STARTED_MESSAGE);
      refreshNotifications();
    } catch (err) {
      // A stale cached run id gives 404; refresh it so a retry uses the latest run
      if (err?.response?.status === 404) {
        fetchLatestDiscoveryRunId(projectId).catch((e) => console.error(e));
      }
      throw err;
    } finally {
      setCompilingAll(false);
    }
  };

  const handleEditCase = (row) => {
    if (usesDiscoveryFlow(row)) {
      setStepsEditCase(row);
      return;
    }
    setEditingTestCaseId(row.id);
    setExtensionMode('edit');
    setExtensionOpen(true);
  };

  const columns = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={cases.length > 0 && selectedIds.length === cases.length}
          onChange={toggleSelectAll}
          aria-label="Select all"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: 40,
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() => toggleSelect(row.id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.name}`}
        />
      ),
    },
    {
      key: 'sr',
      header: 'Sr.No',
      width: 56,
      render: (row) => {
        const idx = cases.findIndex((c) => c.id === row.id);
        return (page - 1) * pageSize + (idx >= 0 ? idx : 0) + 1;
      },
    },
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <div>
          <button
            type="button"
            className="rt2-link"
            onClick={(e) => {
              e.stopPropagation();
              openCase(row);
            }}
          >
            {row.name}
          </button>
          {row.description && (
            <div className="rt2-muted" style={{ marginTop: 2 }}>
              {String(row.description).slice(0, 72)}
              {String(row.description).length > 72 ? '…' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'environment',
      header: 'Environment',
      render: (row) => row.environment || row.browser || '—',
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => <PriorityBadge priority={row.priority} />,
    },
    {
      key: 'self_heal',
      header: 'Self-Heal',
      render: (row) => (
        <span className={`rt-badge ${row.is_healed ? 'rt-badge--info' : 'rt-badge--neutral'}`}>
          {row.is_healed ? 'Healed' : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 160,
      render: (row) => {
        const isDiscoveryFlow = usesDiscoveryFlow(row);
        const isScriptGenerated = row.automation_status === 'generated';
        const isCompiling = compilingCaseId === row.id;
        // Discovery flow test cases need a compiled script before they can run
        const canRun = !isDiscoveryFlow || isScriptGenerated;
        const compileTitle = isScriptGenerated
          ? 'Recompile and generate script'
          : 'Compile and generate script';

        return (
          <RowActions
            primary={
              <>
                {isDiscoveryFlow && (
                  <button
                    type="button"
                    className="rt-btn rt-btn--tertiary rt-btn--icon rt-btn--sm"
                    onClick={() => handleCompileScript(row)}
                    disabled={isCompiling || compilingAll}
                    title={compileTitle}
                    aria-label={`${compileTitle} for ${row.name}`}
                  >
                    {isCompiling ? (
                      <LuLoaderCircle size={16} className="rt-spin" />
                    ) : isScriptGenerated ? (
                      <LuRefreshCw size={16} />
                    ) : (
                      <LuCode size={16} />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="rt-btn rt-btn--secondary rt-btn--sm"
                  onClick={() => setRunCase(row)}
                  disabled={!canRun}
                  title={canRun ? 'Run test case' : 'Compile and generate the script first'}
                  aria-label={`Run ${row.name}`}
                >
                  Run
                </button>
              </>
            }
            menuItems={[
              { id: 'view', label: 'View', onClick: () => openCase(row) },
              {
                id: 'edit',
                label: 'Edit',
                onClick: () => handleEditCase(row),
              },
              {
                id: 'delete',
                label: 'Delete',
                danger: true,
                dividerBefore: true,
                onClick: () => setDeleteCase(row),
              },
            ]}
          />
        );
      },
    },
  ];

  const chips = [];
  if (status !== 'all') chips.push({ key: 'status', label: `Status: ${status}` });
  if (priority !== 'all') chips.push({ key: 'priority', label: `Priority: ${priority}` });
  if (type !== 'all') chips.push({ key: 'type', label: `Type: ${TYPE_FILTERS[type].label}` });
  if (search.trim()) chips.push({ key: 'search', label: `Search: ${search.trim()}` });

  return (
    <PageScaffold
      title="Test Cases"
      description="Manage, automate and monitor your test coverage."
      breadcrumbs={<Breadcrumbs items={[{ label: 'Build' }, { label: 'Test Cases' }]} />}
      actions={
        <>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            onClick={handleExport}
            disabled={!projectId || exporting}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            title="Import test cases from an Excel sheet"
            onClick={() => setImportOpen(true)}
            disabled={!projectId}
          >
            Import
          </button>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={load} disabled={loading}>
            Refresh
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            title="Compile and generate script for all AI generated and manual test cases"
            onClick={() => setCompileOptionsOpen(true)}
            disabled={!projectId || compilingAll}
          >
            {compilingAll ? (
              <LuLoaderCircle size={14} className="rt-spin" />
            ) : (
              <LuCode size={14} />
            )}
            {compilingAll ? 'Generating…' : 'Compile and Generate'}
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--primary"
            disabled={!projectId}
            onClick={() => setCreateOptionsOpen(true)}
          >
            + Create Test Case
          </button>
        </>
      }
    >
      <div className="rt-context-strip">
        <div>
          <h2 className="rt-context-strip__title">{projectName}</h2>
          <div className="rt-context-strip__meta">
            <span className="rt-badge rt-badge--neutral">Desktop</span>
            <StatusBadge status="active">Active</StatusBadge>
          </div>
        </div>
        <div>
          <div className="rt-context-strip__stat-label">Test Cases</div>
          <div className="rt-context-strip__stat-value">{loading ? '…' : total.toLocaleString()}</div>
        </div>
        <div>
          <div className="rt-context-strip__stat-label">Pass Rate</div>
          <div className="rt-context-strip__stat-value">{loading ? '…' : passRate}</div>
        </div>
        <div>
          <div className="rt-context-strip__stat-label">Failed</div>
          <div className="rt-context-strip__stat-value">{loading ? '…' : metrics.failed}</div>
        </div>
        <div>
          <div className="rt-context-strip__stat-label">Pending</div>
          <div className="rt-context-strip__stat-value">{loading ? '…' : metrics.pending}</div>
        </div>
      </div>

      <FilterBar>
        <SearchInput
          value={searchDraft}
          onChange={setSearchDraft}
          placeholder="Search test cases…"
        />
        <Select
          className="rt-select--filter"
          value={status}
          onChange={(e) => setParam('status', e.target.value)}
          aria-label="Status filter"
        >
          <option value="all">All statuses</option>
          <option value="passed">Passed</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </Select>
        <Select
          className="rt-select--filter"
          value={priority}
          onChange={(e) => setParam('priority', e.target.value)}
          aria-label="Priority filter"
        >
          <option value="all">All priorities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Select>
        <Select
          className="rt-select--filter"
          value={type}
          onChange={(e) => setParam('type', e.target.value)}
          aria-label="Type filter"
        >
          <option value="all">All types</option>
          {Object.entries(TYPE_FILTERS).map(([key, opt]) => (
            <option key={key} value={key}>{opt.label}</option>
          ))}
        </Select>
        {activeFilterCount > 0 && (
          <button type="button" className="rt-btn rt-btn--tertiary rt-btn--sm" onClick={clearFilters}>
            Clear
          </button>
        )}
      </FilterBar>

      {chips.length > 0 && (
        <div className="rt-filter-chips">
          {chips.map((chip) => (
            <span key={chip.key} className="rt-filter-chip">
              {chip.label}
              <button
                type="button"
                aria-label={`Remove ${chip.label}`}
                onClick={() => {
                  if (chip.key === 'search') {
                    setSearchDraft('');
                    setSearch('');
                  } else {
                    setParam(chip.key, LIST_SCHEMA[chip.key]);
                  }
                }}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" className="rt-btn rt-btn--tertiary rt-btn--sm" onClick={() => {
            setSearchDraft('');
            clearFilters();
          }}>
            Clear all
          </button>
        </div>
      )}

      {error && !cases.length && !loading && (
        <ErrorState
          title="Unable to load test cases"
          description={
            error?.includes('Select a project')
              ? 'Please select a project before continuing.'
              : error
          }
          onRetry={load}
        />
      )}

      <DataTable
        columns={columns}
        rows={cases}
        rowKey="id"
        onRowClick={openCase}
        loading={loading}
        emptyTitle="No test cases found"
        emptyDescription="Try changing your filters or search, or create your first test case."
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={pageSizeOptions}
        loading={loading}
      />

      <BulkActionBar count={selectedIds.length} onClear={() => setSelectedIds([])}>
        <button
          type="button"
          className="rt-btn rt-btn--secondary rt-btn--sm"
          disabled={deleting}
          onClick={handleBulkDelete}
        >
          {deleting ? 'Deleting…' : 'Delete selected'}
        </button>
      </BulkActionBar>

      <CompileScriptsOptionsModal
        isOpen={compileOptionsOpen}
        onClose={() => setCompileOptionsOpen(false)}
        onGenerate={handleCompileAllScripts}
      />

      <CreateTestCaseOptionsModal
        isOpen={createOptionsOpen}
        onClose={() => setCreateOptionsOpen(false)}
        onCreateTestCase={() => {
          setCreateOptionsOpen(false);
          setCreateFormOpen(true);
        }}
        onCreateViaExtension={() => {
          setCreateOptionsOpen(false);
          setEditingTestCaseId(null);
          setExtensionMode('create');
          setExtensionOpen(true);
        }}
        projectId={projectId}
        projectName={projectName}
      />

      <ExtensionRecordingModal
        isOpen={extensionOpen}
        onClose={() => {
          setExtensionOpen(false);
          setExtensionMode('create');
          setEditingTestCaseId(null);
        }}
        onRecordingStarted={() => {
          recordingRefreshPending.current = true;
        }}
        onTestCaseUpdated={async () => {
          setExtensionOpen(false);
          setEditingTestCaseId(null);
          await load();
        }}
        mode={extensionMode}
        testCaseId={editingTestCaseId}
        projectId={projectId}
        projectName={projectName}
      />

      <CreateTestCaseModal
        isOpen={createFormOpen}
        onClose={() => setCreateFormOpen(false)}
        onCreateTestCase={async () => {
          setCreateFormOpen(false);
          await load();
        }}
        projectId={projectId}
        projectName={projectName}
      />

      <ImportTestCasesModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={async (_projectId, response) => {
          setImportOpen(false);
          toast.success(response?.message || 'Test cases imported successfully');
          await load();
        }}
        projectId={projectId || ''}
        projectName={projectName}
      />

      {/* Manual / AI generated test cases run through the UI Discovery flow */}
      <RunDiscoveryTestCaseModal
        isOpen={Boolean(runCase) && usesDiscoveryFlow(runCase)}
        onClose={() => setRunCase(null)}
        onSuccess={handleDiscoveryRunSuccess}
        testCaseId={runCase?.id}
        testCaseName={runCase?.name}
      />

      <EditDiscoveryStepsModal
        isOpen={Boolean(stepsEditCase)}
        onClose={() => setStepsEditCase(null)}
        onUpdated={async () => {
          toast.success('Test steps updated successfully');
          await load();
        }}
        testCase={stepsEditCase}
      />

      {runCase && !usesDiscoveryFlow(runCase) && (
        <RunTestCaseModal
          isOpen={Boolean(runCase)}
          onClose={() => setRunCase(null)}
          onSuccess={() => {
            setRunCase(null);
            load();
          }}
          testCaseId={runCase.id}
          testCaseName={runCase.name}
          projectId={projectId}
          projectName={projectName}
          ai_generated={runCase.ai_generated}
          environment={runCase.environment}
          environmentId={runCase.environment_id}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteCase)}
        title="Delete test case?"
        body={`Delete "${deleteCase?.name}"? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onCancel={() => setDeleteCase(null)}
        onConfirm={confirmDelete}
      />
    </PageScaffold>
  );
};

export default CasesWorkspace;
